import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { RecentActivityAPIItem } from '../../../../../../api/useSupplierReconciliation/types/recentActivity'
import type { RecentActivityData } from '../types/supplier'
import { formatters, getUserTimezone } from '@utils/intl/dateTimeFormatters'

dayjs.extend(utc)
dayjs.extend(timezone)

// Map activity content types to user-friendly descriptions
function getActivityDescription(item: RecentActivityAPIItem): string {
  const { content, extra_data, item_type } = item
  const supplierName = extra_data.supplier_name || 'Unknown Supplier'

  // Handle comment activities differently
  if (item_type === 'comment') {
    return content // For comments, content is the actual comment text
  }

  // Handle activity types
  switch (content) {
    case 'invoice_added':
      return `Invoice ${extra_data.invoice_number} received from ${supplierName} and ready for processing`

    case 'invoice_removed':
      return `Activity: invoice removed for ${supplierName}`

    case 'bol_added':
      return `BOL ${extra_data.bol_number} received from ${supplierName} and ready for matching`

    case 'match_approved':
      return `Match approved for ${supplierName}`

    case 'hold_set':
      return `Match placed on hold for ${supplierName}${extra_data.hold_reason ? `: ${extra_data.hold_reason}` : ''}`

    case 'hold_removed':
      return `Hold removed for ${supplierName}`

    case 'invoice_voided':
      return `Invoice ${extra_data.invoice_number} voided for ${supplierName}`

    case 'invoice_unvoided':
      return `Invoice ${extra_data.invoice_number} unvoided for ${supplierName}`

    case 'invoice_matched':
      return `Invoice ${extra_data.invoice_number} successfully matched with BOL`

    case 'invoice_approved':
      return `Invoice ${extra_data.invoice_number} approved and ready for payment`

    default:
      return `Activity: ${content.replace(/_/g, ' ')} for ${supplierName}`
  }
}

/**
 * Transform Recent Activity API response to action-based format
 * Updated to use action types instead of status inference
 */
export function transformRecentActivity(apiItems: RecentActivityAPIItem[]): RecentActivityData[] {
  if (!apiItems?.length) return []

  return apiItems.map((item) => {
    const description = getActivityDescription(item)

    // Extract invoice number for navigation - prioritize invoice_number, fallback to bol_number
    const invoiceNumber = item.extra_data.invoice_number || item.extra_data.bol_number || ''

    // For display, show invoice number if available, otherwise "Match"
    const displayId = invoiceNumber || 'need invoice#'

    return {
      id: item.id,
      displayId,
      actionType: item.item_type === 'comment' ? 'comment' : item.content, // Use 'comment' for comments, actual action type for activities
      description,
      personName: item.username,
      timestamp:
        formatters.WEEKDAY_MONTH_DATE_TIME(dayjs.utc(item.created_on).tz(getUserTimezone()), getUserTimezone()) ||
        dayjs(item.created_on).format('MMM D, YYYY - h:mm A'),
      matchId: item.extra_data.match_id || '',
      invoiceNumber,
    }
  })
}
